import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Music2, X, Volume2, VolumeX } from "lucide-react";
import { Slider } from "./ui/slider";
import { siteMusic, siteMusicTitle } from "../mock";

const DEFAULT_VOLUME = 0.35; // gentle background ambiance

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [dismissed, setDismissed] = useState(false);

  // Autostart WITH SOUND. Browsers block unmuted autoplay until the visitor
  // interacts with the page, so we try to play with sound immediately and, if
  // blocked, start with sound on the very first interaction (click/scroll/key).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = DEFAULT_VOLUME;
    audio.muted = false;

    const playWithSound = () => {
      audio.muted = false;
      setMuted(false);
      return audio.play().then(() => setPlaying(true)).catch(() => false);
    };

    playWithSound().then((ok) => {
      if (ok === false) {
        const onFirst = () => {
          playWithSound();
          window.removeEventListener("pointerdown", onFirst);
          window.removeEventListener("keydown", onFirst);
          window.removeEventListener("scroll", onFirst);
        };
        window.addEventListener("pointerdown", onFirst, { once: true });
        window.addEventListener("keydown", onFirst, { once: true });
        window.addEventListener("scroll", onFirst, { once: true });
      }
    });

    return () => {};
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.muted = false;
      setMuted(false);
      audio.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !audio.muted;
    audio.muted = next;
    setMuted(next);
  };

  const changeVolume = (v) => {
    const audio = audioRef.current;
    const vol = v / 100;
    setVolume(vol);
    if (audio) {
      audio.volume = vol;
      if (vol > 0 && audio.muted) {
        audio.muted = false;
        setMuted(false);
      }
    }
  };

  const close = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setPlaying(false);
    setDismissed(true);
  };

  if (dismissed) return null;

  const isQuiet = muted || volume === 0;

  return (
    <div className="fixed z-40 top-[78px] right-4 sm:right-8">
      <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md shadow-soft rounded-full pl-1.5 pr-2.5 py-1.5 border border-border">
        <button
          onClick={toggle}
          aria-label={playing ? "Pause music" : "Play music"}
          className="btn-cta grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground shrink-0"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>

        <div className="hidden sm:flex items-center gap-2.5">
          <div className="leading-tight">
            <p className="text-[11px] font-bold text-foreground flex items-center gap-1">
              <Music2 className="h-3 w-3 text-accent" /> {siteMusicTitle}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {!playing ? "Paused" : isQuiet ? "Muted" : "Now playing"}
            </p>
          </div>
          <div className={`eq ${playing && !isQuiet ? "" : "paused"}`} aria-hidden="true">
            <span /><span /><span /><span />
          </div>

          <button
            onClick={toggleMute}
            aria-label={isQuiet ? "Unmute" : "Mute"}
            className="grid place-items-center h-7 w-7 rounded-full text-primary hover:bg-secondary transition-colors shrink-0"
          >
            {isQuiet ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <Slider
            value={[isQuiet ? 0 : Math.round(volume * 100)]}
            onValueChange={([v]) => changeVolume(v)}
            max={100}
            step={1}
            aria-label="Volume"
            className="w-16"
          />
        </div>

        <button
          onClick={close}
          aria-label="Turn off music"
          className="grid place-items-center h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <audio ref={audioRef} src={siteMusic} loop preload="auto" />
    </div>
  );
};

export default MusicPlayer;
